import {
  BanknotesIcon,
  DocumentTextIcon,
  MagnifyingGlassIcon,
  PlusIcon,
  FunnelIcon,
} from '@heroicons/react/24/outline'
import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { ProgramCreation } from '../../components/ayuda/modals/ProgramCreation'
import Pagination from '../../components/common/Pagination'
import { useQuery, useQueryClient } from 'react-query'
import { ayudaApi } from '../../services/api'
import StatisticsCard from '../../components/common/StatisticsCard'
import {
  ayudaProgramColumns,
  buildAyudaStats,
  capitalize,
  formatAmount,
  formatDate,
  statusStyles,
} from '../../components/ayuda/const/const'
import { AuditTrailModal } from '../../components/ayuda/modals/AuditTrail'
import { useAuthStore } from '../../stores/authStore'
import Table from '../../components/common/Table'
import { Filter } from '../../components/ayuda/action/Filter'
import { ProgramCard } from '../../components/ayuda/cards/ProgramCard'

function AyudaPage() {
  const navigate = useNavigate()
  const { user, isAdmin, isBarangayOfficial } = useAuthStore()
  const queryClient = useQueryClient()
  const [showForm, setShowForm] = useState(false)
  const [search, setSearch] = useState('')
  const [debouncedSearch, setDebouncedSearch] = useState('')
  const [currentPage, setCurrentPage] = useState(1)
  const [perPage, setPerPage] = useState(10)
  const [showFilters, setShowFilters] = useState(false)
  const [filters, setFilters] = useState({
    coverage: '',
    beneficiary: '',
    status: '',
  })
  const [showAuditTrail, setShowAuditTrail] = useState(false)

  const shouldHideButtons = isAdmin() || isBarangayOfficial()
  const showTableView = isAdmin() || isBarangayOfficial()

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search)
      setCurrentPage(1)
    }, 400)

    return () => clearTimeout(timer)
  }, [search])

  const { data, isLoading, isFetching } = useQuery({
    queryKey: [
      'ayuda-programs',
      debouncedSearch,
      currentPage,
      perPage,
      filters,
    ],
    queryFn: () => {
      const payload = {
        per_page: perPage,
        search: debouncedSearch || undefined,
        page: currentPage,
        coverage:
          filters.coverage === ''
            ? undefined
            : filters.coverage === 'true'
            ? true
            : false,
        beneficiary:
          filters.beneficiary === ''
            ? undefined
            : filters.beneficiary === 'true'
            ? true
            : false,
        status: filters.status || undefined,
      }

      return ayudaApi.getAyuda(payload)
    },
    keepPreviousData: true,
  })
  const { data: statsData, isLoading: isStatsLoading } = useQuery({
    queryKey: ['ayuda-program-stats'],
    queryFn: ayudaApi.getAyudaStats,
  })

  const statsAyuda = buildAyudaStats(statsData)
  const programs = data?.data?.data?.ayuda_programs ?? []
  const meta = data?.data?.data?.meta ?? {}

  const handlePageChange = (page, newPerPage) => {
    if (newPerPage && newPerPage !== perPage) {
      setPerPage(newPerPage)
      setCurrentPage(1)
    } else {
      setCurrentPage(page)
    }
  }

  const handleProgramSuccess = () => {
    queryClient.invalidateQueries(['ayuda-programs'])
    setShowForm(false)
  }

  useEffect(() => {
    setCurrentPage(1)
  }, [filters])

  return (
    <div className='h-full bg-gray-50 animate-in fade-in duration-1000'>
      <div className='max-w-9xl mx-auto p-1'>
        <div className='flex flex-col sm:flex-row sm:items-center sm:justify-between mb-8'>
          <div className='flex items-center space-x-4 mb-4 sm:mb-0'>
            <div className='flex items-center justify-center w-12 h-12 bg-blue-100 rounded-lg animate-pulse'>
              <BanknotesIcon className='w-6 h-6 text-blue-600' />
            </div>
            <div>
              <h1 className='text-2xl font-bold text-gray-900'>
                Ayuda Programs
              </h1>
              <p className='text-gray-600'>
                Provides temporary government assistance to people in need
                during emergencies or hardships.
              </p>
            </div>
          </div>

          <div className='flex flex-col sm:flex-row sm:items-center sm:space-x-3 space-y-3 sm:space-y-0'>
            <button
              onClick={() => setShowAuditTrail(true)}
              className='inline-flex items-center px-4 py-2 rounded-md text-sm font-medium text-white bg-gray-600 hover:bg-gray-700'
            >
              <DocumentTextIcon className='w-5 h-5 mr-2' />
              Audit Trail
            </button>
            {!shouldHideButtons && (
              <button
                onClick={() => setShowForm(true)}
                className='inline-flex items-center px-4 py-2 rounded-md text-sm font-medium text-white bg-blue-600 hover:bg-blue-700'
              >
                <PlusIcon className='w-3 h-3 mr-2' />
                New Program
              </button>
            )}
          </div>

          {showForm && (
            <ProgramCreation
              setShowForm={setShowForm}
              resetProgramForm={() => setShowForm(false)}
              onSuccess={handleProgramSuccess}
            />
          )}
          {showAuditTrail && (
            <AuditTrailModal setShowModal={setShowAuditTrail} />
          )}
        </div>
        <StatisticsCard stats={statsAyuda} />

        <div className='rounded-xl shadow-sm'>
          {showTableView && (
            <div className='flex items-center justify-between gap-4 p-4'>
              <h3 className='text-lg font-medium text-gray-900'>
                Ayuda Programs
              </h3>
              <div className='flex items-center gap-2'>
                <div className='relative w-full sm:w-80'>
                  <input
                    type='text'
                    placeholder='Search ayuda programs...'
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    className='w-full rounded-lg border border-gray-300 px-4 py-2 text-sm focus:border-blue-500 focus:ring-2 focus:ring-blue-500'
                  />
                </div>
                <button
                  className={`inline-flex items-center px-4 py-2 border rounded-lg text-sm font-medium transition-colors
                  ${showFilters ? 'border-blue-500' : 'border-gray-300'}`}
                  onClick={() => setShowFilters(!showFilters)}
                >
                  <FunnelIcon
                    className={`w-4 h-4 mr-2 transition-colors ${
                      showFilters ? 'text-blue-500' : 'text-gray-500'
                    }`}
                  />
                  Filters
                </button>
              </div>
            </div>
          )}

          {showFilters && <Filter setFilters={setFilters} filters={filters} />}

          {/* {isLoading ? (
            <div className='text-center text-gray-500 py-20'>
              Loading ayuda programs...
            </div>
          ) : programs.length === 0 ? (
            <div className='text-center text-gray-500 py-20'>
              No ayuda programs found.
            </div>
          ) : showTableView ? (
            <Table
              columns={ayudaProgramColumns(
                navigate,
                formatAmount,
                formatDate,
                meta
              )}
              data={programs}
              getRowKey={(program) => program.id}
              pagination={{
                currentPage: meta.current_page,
                lastPage: meta.last_page,
                perPage: meta.per_page,
                total: meta.total,
              }}
              onPageChange={handlePageChange}
              paginationProps={{
                isLoading: isFetching,
                showSizeSelector: true,
              }}
            />
          ) : ( */}
          {isLoading ? (
            <div className='text-center text-gray-500 py-20'>
              Loading ayuda programs...
            </div>
          ) : showTableView ? (
            <Table
              columns={ayudaProgramColumns(
                navigate,
                formatAmount,
                formatDate,
                meta
              )}
              data={programs}
              getRowKey={(program) => program.id}
              pagination={{
                currentPage: meta.current_page,
                lastPage: meta.last_page,
                perPage: meta.per_page,
                total: meta.total,
              }}
              onPageChange={handlePageChange}
              paginationProps={{
                isLoading: isFetching,
                showSizeSelector: true,
              }}
              emptyMessage="No ayuda programs found."
            />
          ) : (
            <>
              <div className='grid grid-cols-1 max-h-96 overflow-y-auto sm:grid-cols-2 lg:grid-cols-3 gap-6'>
                {programs.map((program) => (
                  <ProgramCard
                    key={program.id}
                    program={program}
                    navigate={navigate}
                    meta={meta}
                    formatDate={formatDate}
                    statusStyles={statusStyles}
                    capitalize={capitalize}
                  />
                ))}
              </div>
              <div className='mt-6'>
                <Pagination
                  isLoading={isFetching}
                  currentPage={meta.current_page}
                  lastPage={meta.last_page}
                  perPage={meta.per_page}
                  total={meta.total}
                  onPageChange={handlePageChange}
                />
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  )
}

export default AyudaPage
